import React, { useEffect, useState } from "react";
import {
    LightModeOutlined,
    DarkModeOutlined,
    Menu as MenuIcon,
    Search,
    SettingsOutlined,
    ArrowDropDownOutlined,
    Logout,
} from "@mui/icons-material";
import FlexBetween from "./FlexBetween";
import { useDispatch, useSelector } from "react-redux";
import { RootState, setMode } from "state";
import profileImage from "assets/profile.png";
import { useNavigate } from "react-router-dom";
import {
    AppBar,
    Box,
    Button,
    IconButton,
    InputBase,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
    useTheme,
    Autocomplete,
    TextField,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import LoginButton from "./LoginButton";
import LogoutButton from "./LogoutButton";
import { AuthUser } from "state/authSlice";
import { useGetStudentsQuery } from "state/api";
import SchoolDashboardSummary from "./SchoolDashboardSummary";

interface NavbarProps {
    isSidebarOpen: boolean;
    setIsSidebarOpen: (isOpen: boolean) => void;
    user: AuthUser | null;
}

function capitalizeFirstLetter(text: string) {
    if (!text) return ''; // Handle empty or null strings
    return text.charAt(0).toUpperCase() + text.slice(1);
}

const Navbar = ({ isSidebarOpen, setIsSidebarOpen, user }: NavbarProps) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isOpen = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const navigate = useNavigate();
    // TODO: Figure out why this is not working. I'll just use useAuth0 for now.
    // const { isAuthenticated, isLoading } = useSelector(
    //   (state: RootState) => state.auth
    // );
    const { logout } = useAuth0();
    const [skip, setSkip] = useState(true);
    const { schoolId, schoolName } = useSelector(
        (state: RootState) => state.admin
    );
    const [id, setId] = useState(0);
    const [name, setName] = useState("");

    useEffect(() => {
        if (user && user.isAdmin && schoolId > 0) {
            console.log("School Id: ", schoolId);
            setSkip(false);
            setId(schoolId);
            if (schoolName) {
                setName(schoolName);
            }
        }
        else if (user && user.school_id > 0) {
            setSkip(false);
            setId(user.school_id);
            if (user.school_name) {
                setName(user.school_name);
            }
        }
    }, [schoolId, schoolName, user]);
    const { data: studentsData, isLoading: isStudentsDataLoading } =
        useGetStudentsQuery(Number(schoolId > 0 ? schoolId : user?.school_id), {
            skip,
        });
    const [searchTerm, setSearchTerm] = useState('');

    const [autoCompletevalue, setValue] = useState('');
    const [autoCompleteinputValue, setInputValue] = useState('');

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        console.log("SearchApp rendered with searchTerm:", searchTerm);
    };
    const studentslist = studentsData?.map(student => student.name) || [];
    const navigateToStudentDetail = (studentName: string) => {
        const student = studentsData?.find(s => s.name === studentName);
        if (student) {
            navigate(`/studentDetail/${student.id}`);
        }
    }

    return (
        <AppBar
            sx={{
                position: "static",
                background: "none",
                boxShadow: "none",
                width: "100%",
                height: '75px',
                border: `1px solid ${theme.palette.secondary[200]}`,
            }}
        >
            <Toolbar sx={{ justifyContent: "space-between" }}>
                {/* Left Side*/}
                <FlexBetween>

                    <Box display="flex" alignItems="center" gap="0.5rem">
                        <img
                            src="../IsoKinder_logo_without_background.png"
                            alt="IsoKinder Logo"
                            style={{ width: '120px' }}
                        />
                    </Box>


                    <Box marginBottom="20px">
                    <Autocomplete
                        clearOnEscape
                        autoComplete
                        options={studentslist}
                        value={autoCompletevalue}
                        inputValue={autoCompleteinputValue}
                        onInputChange={(event, newInputValue) => {
                            setInputValue(newInputValue);
                        }}
                        size="small"
                        onChange={(event, newValue) => {
                            setValue(newValue || '');
                            console.log("Selected student:", newValue);
                            // Navigate to student detail page
                            if (!newValue) return; // Handle case where no value is selected
                            navigateToStudentDetail(newValue);
                            setValue('');
                            setInputValue('');
                        }}

                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search Students"
                                variant="outlined"


                                sx={{

                                    width: '300px',
                                    height: '37px',
                                    fontSize: '0.875rem',
                                    fontcolor: theme.palette.secondary[200],
                                    borderRadius: '20px',
                                    position: 'absolute',
                                    left: '25%',
                                    transform: 'translateX(-25%)',
                                    backgroundColor: theme.palette.background.alt,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '20px',
                                    },
                                    // Style the label
                                    '& .MuiInputLabel-root': {
                                        fontSize: '1rem', // Change font size
                                        color: theme.palette.secondary[200], // Change color
                                    },
                                    // Style the label when focused
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: theme.palette.primary.main, // Change color when focused
                                    },
                                }}
                                fullWidth
                            />
                        )}
                        />
                    </Box>


                </FlexBetween>
                {/* Right Side */}
                <FlexBetween gap="1.5rem">

                    <FlexBetween>
                        <Button
                            onClick={handleClick}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                textTransform: "none",
                                gap: "1rem",
                            }}
                        >
                            <Box
                                component="img"
                                alt="profile"
                                src={user?.picture}
                                height="32px"
                                width="32px"
                                borderRadius="50%"
                                sx={{ objectFit: "cover" }}
                                onError={e => {
                                    const target = e.currentTarget as HTMLImageElement;
                                    target.onerror = null; // Prevent infinite loop
                                    target.src = "../user.png";
                                }}

                            />
                            <Box textAlign="left">

                                <Typography fontWeight="bold" fontSize="0.85rem"
                                    sx={{ color: theme.palette.secondary[200] }}
                                >
                                    {capitalizeFirstLetter(user?.name ?? "")}
                                </Typography>
                            </Box>
                            <ArrowDropDownOutlined
                                sx={{ color: theme.palette.secondary[300], fontSize: "15px" }}
                            />
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={isOpen}
                            onClose={handleClose}
                            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                        >
                            <MenuItem>
                                <IconButton onClick={() => logout()}>
                                    <Logout />
                                    <Typography
                                        fontWeight="bold"
                                        fontSize="0.85rem"
                                        sx={{ color: theme.palette.secondary[200] }}
                                    >
                                        Logout
                                    </Typography>
                                </IconButton>
                            </MenuItem>
                        </Menu>
                    </FlexBetween>
                    {/* {isAuthenticated ? <LogoutButton /> : <LoginButton />} */}
                </FlexBetween>
            </Toolbar>
        </AppBar>
    );
};

export default Navbar;
